/* eslint-disable default-case */
import React, { FC, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Example from './components/Example'
import Members from './components/pages/Members'
import { AllMembers } from './components/pages/AllMembers'
import { Updates } from './components/pages/Updates'
import { Workplaces } from './components/pages/Workplaces'
import './theme/global.css'
import { useContainerStyles } from './theme/layout'
import { MyProfile } from './components/pages/MyProfile'
import { MemberProfile } from './components/pages/MemberProfile'
import { MemberProfileFromSearch } from './components/pages/MemberProfileFromSearch'
import { Footer } from './components/ui/Footer/Footer'
import { authRoles, canAccessLoenindplaceringer } from './store/auth'
import { CardWithIconAndMessge } from './components/ui/Card/CardWithIconAndMessge'
import { LockFill } from './assets/icons'
import { colors } from './theme/palette'
import { NotePage } from './components/pages/MyNotes/NotePage'
import { ScrollToTopButton } from './components/ui/ScrollToTopButton/ScrollToTopButton'
import breakpoints from './theme/breakpoints'
import { Organisation } from './components/pages/Organization'
import { Salary } from './components/pages/Salary'
import { CaseDetails } from './components/pages/Salary/CaseDetails'
import { CaseRedirect } from './components/pages/Salary/CaseRedirect'
import { L } from './components/providers/Lokalise'

const ScrollToTop: FC = ({ children }) => {
    const location = useLocation()
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [location])

    return <>{children}</>
}

const useStyles = createUseStyles({
    circleIcon: {
        width: '62px',
        height: '62px',
        borderRadius: '50%',
        border: `2px solid ${colors.white}`,
        backgroundColor: colors.blue300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
    },
    content: {
        paddingTop: '5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    messageWrapper: {
        marginTop: '2.25rem',
    },
    mobileOnly: {
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            display: 'none',
        },
    },
})

const RoleBasedRoutes: React.FC = (): JSX.Element => {
    const [roles] = useAtom(authRoles)
    const [_canAccessLoenindplaceringer] = useAtom(canAccessLoenindplaceringer)
    const layout = useContainerStyles()
    const classes = useStyles()

    if (roles.indexOf('AktivBruger') === -1) {
        return (
            <div className={`${layout.container} main-content`}>
                <div className={`${classes.content} page-up`}>
                    <div className={classes.messageWrapper}>
                        <CardWithIconAndMessge
                            icon={
                                <div className={classes.circleIcon}>
                                    <LockFill
                                        color={colors.green500}
                                        width="32px"
                                    />
                                </div>
                            }
                            message="Siden er begrænset af profilen. Vælg en anden rolle på knappen til højre øverst."
                        />
                    </div>
                </div>
            </div>
        )
    }

    // Component for restricted access message
    const RestrictedAccessMessage: React.FC = () => (
        <div className={`${layout.container} main-content`}>
            <div className={`${classes.content} page-up`}>
                <div className={classes.messageWrapper}>
                    <CardWithIconAndMessge
                        icon={
                            <div className={classes.circleIcon}>
                                <LockFill
                                    color={colors.green500}
                                    width="32px"
                                />
                            </div>
                        }
                        message={L('LØN_BRUGER_IKKE_TILGANG_TIL_SIDE')}
                    />
                </div>
            </div>
        </div>
    )

    return (
        <>
            <ScrollToTop>
                <div
                    className={`${layout.container} main-content`}
                    id="main-content"
                >
                    <Routes>
                        <Route path="/mine-medlemmer" element={<Members />} />
                        <Route
                            path="/mine-medlemmer/:id"
                            element={<MemberProfile />}
                        />
                        <Route
                            path="/alle-foas-medlemmer"
                            element={<AllMembers />}
                        />
                        <Route
                            path="/mine-opdateringer"
                            element={<Updates />}
                        />
                        <Route path="/mine-noter" element={<NotePage />} />
                        <Route
                            path="/mine-arbejdsteder"
                            element={<Workplaces />}
                        />
                        <Route path="/style-guide" element={<Example />} />
                        <Route path="/min-profil" element={<MyProfile />} />
                        <Route
                            path="/organisering"
                            element={<Organisation />}
                        />

                        <Route
                            path="/alle-foas-medlemmer/:id"
                            element={<MemberProfileFromSearch />}
                        />

                        {/* Link format used in emails: /sag?sagsId=<id> */}
                        <Route path="/sag" element={<CaseRedirect />} />

                        {_canAccessLoenindplaceringer ? (
                            <>
                                <Route
                                    path="/mine-lonindplaceringer"
                                    element={<Salary />}
                                />
                                <Route
                                    path="/mine-lonindplaceringer/sag"
                                    element={<CaseDetails />}
                                />
                                <Route
                                    path="/mine-lonindplaceringer/sag/:id"
                                    element={<CaseDetails />}
                                />
                            </>
                        ) : (
                            <>
                                <Route
                                    path="/mine-lonindplaceringer"
                                    element={<RestrictedAccessMessage />}
                                />
                                <Route
                                    path="/mine-lonindplaceringer/sag"
                                    element={<RestrictedAccessMessage />}
                                />
                                <Route
                                    path="/mine-lonindplaceringer/sag/:id"
                                    element={<RestrictedAccessMessage />}
                                />
                            </>
                        )}

                        <Route
                            path="/"
                            element={<Navigate replace to="/mine-medlemmer" />}
                        />
                    </Routes>
                </div>

                <ScrollToTopButton />

                <Footer />
            </ScrollToTop>
        </>
    )
}

export default RoleBasedRoutes
